import axios from 'axios';
import { 
  ApiResponse, 
  Category, 
  AppListResponse, 
  AppDetail, 
  SystemSettings,
  RatingRequest,
  RatingResponse,
  AppListItem,
  AdPosition,
  AdminAppListResponse,
  CustomList,
  CustomListDetailResponse
} from '../types';
import { config, buildApiUrl, buildAdminApiUrl } from '../config';

// 创建axios实例
const api = axios.create({
  baseURL: buildApiUrl(),
  timeout: config.api.timeout,
});

// 创建管理员API实例
const adminApi = axios.create({
  baseURL: buildAdminApiUrl(),
  timeout: config.api.timeout,
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 管理员API请求拦截器
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 管理员API响应拦截器
adminApi.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// API接口
export const apiService = {
  // 获取分类列表
  getCategories: (): Promise<ApiResponse<Category[]>> => {
    return api.get('/categories');
  },

  // 获取APP列表
  getApps: (params?: {
    category_id?: number;
    page?: number;
    per_page?: number;
    search?: string;
  }): Promise<ApiResponse<AppListResponse>> => {
    return api.get('/apps', { params });
  },

  // 获取推荐APP列表
  getRecommendedApps: (params?: {
    page?: number;
    per_page?: number;
  }): Promise<ApiResponse<AppListResponse>> => {
    return api.get('/apps/recommended', { params });
  },

  // 获取热门APP
  getPopularApps: (limit?: number): Promise<ApiResponse<AppListItem[]>> => {
    return api.get('/apps/popular', { params: { limit } });
  },

  // 获取APP详情
  getAppDetail: (id: number): Promise<ApiResponse<AppDetail>> => {
    return api.get(`/apps/${id}`);
  },

  // 对APP评分
  rateApp: (id: number, data: RatingRequest): Promise<ApiResponse<RatingResponse>> => {
    return api.post(`/apps/${id}/rate`, data);
  },

  // 增加APP下载量
  incrementDownloadCount: (id: number): Promise<ApiResponse<{message: string}>> => {
    return api.post(`/apps/${id}/download`);
  },

  // 获取系统设置
  getSettings: (): Promise<ApiResponse<SystemSettings>> => {
    return api.get('/settings');
  },

  // 管理员登录
  adminLogin: (data: { username: string; password: string }): Promise<ApiResponse<{ token: string }>> => {
    return api.post('/admin/login', data);
  },

  // 管理员分类管理
  adminGetCategories: (): Promise<ApiResponse<Category[]>> => {
    return adminApi.get('/categories');
  },

  adminCreateCategory: (data: { name: string }): Promise<ApiResponse<Category>> => {
    return adminApi.post('/categories', data);
  },

  adminUpdateCategory: (id: number, data: { name: string }): Promise<ApiResponse<Category>> => {
    return adminApi.put(`/categories/${id}`, data);
  },

  adminDeleteCategory: (id: number): Promise<ApiResponse<void>> => {
    return adminApi.delete(`/categories/${id}`);
  },

  // 管理员APP管理
  adminGetApps: (params?: {
    category_id?: number;
    page?: number;
    per_page?: number;
    search?: string;
  }): Promise<ApiResponse<AdminAppListResponse>> => {
    return adminApi.get('/apps', { params });
  },

  adminCreateApp: (data: any): Promise<ApiResponse<any>> => {
    return adminApi.post('/apps', data);
  },

  adminUpdateApp: (id: number, data: any): Promise<ApiResponse<any>> => {
    return adminApi.put(`/apps/${id}`, data);
  },

  adminDeleteApp: (id: number): Promise<ApiResponse<void>> => {
    return adminApi.delete(`/apps/${id}`);
  },

  adminUpdateAppSortOrder: (id: number, sortOrder: number): Promise<ApiResponse<{message: string}>> => {
    return adminApi.put(`/apps/${id}/sort`, { sort_order: sortOrder });
  },

  adminUpdateAppRecommendOrder: (id: number, recommendOrder: number): Promise<ApiResponse<{message: string}>> => {
    return adminApi.put(`/apps/${id}/recommend`, { recommend_order: recommendOrder });
  },

  // 管理员系统设置
  adminGetSettings: (): Promise<ApiResponse<any[]>> => {
    return adminApi.get('/settings');
  },

  adminUpdateSettings: (data: Record<string, string>): Promise<ApiResponse<void>> => {
    return adminApi.put('/settings', data);
  },

  // 管理员密码修改
  adminChangePassword: (data: { old_password: string; new_password: string }): Promise<ApiResponse<void>> => {
    return adminApi.post('/change-password', data);
  },

  // 获取广告位（公开API）
  getAdPositions: (): Promise<ApiResponse<AdPosition[]>> => {
    return api.get('/ad-positions');
  },

  getAdPositionByName: (name: string): Promise<ApiResponse<AdPosition>> => {
    return api.get(`/ad-positions/${name}`);
  },

  // 管理员广告位管理
  adminGetAdPositions: (): Promise<ApiResponse<AdPosition[]>> => {
    return adminApi.get('/ad-positions');
  },

  adminGetAdPosition: (id: number): Promise<ApiResponse<AdPosition>> => {
    return adminApi.get(`/ad-positions/${id}`);
  },

  adminCreateAdPosition: (data: { name: string; code: string; header_code?: string; description: string; is_active: boolean }): Promise<ApiResponse<AdPosition>> => {
    return adminApi.post('/ad-positions', data);
  },

  adminUpdateAdPosition: (id: number, data: { name: string; code: string; header_code?: string; description: string; is_active: boolean }): Promise<ApiResponse<AdPosition>> => {
    return adminApi.put(`/ad-positions/${id}`, data);
  },

  adminDeleteAdPosition: (id: number): Promise<ApiResponse<void>> => {
    return adminApi.delete(`/ad-positions/${id}`);
  },

  adminInitializeAdPositions: (): Promise<ApiResponse<void>> => {
    return adminApi.post('/ad-positions/initialize');
  },

  // 自定义列表API（公开API）
  getCustomListDetail: (id: number, params?: {
    page?: number;
    per_page?: number;
  }): Promise<ApiResponse<CustomListDetailResponse>> => {
    return api.get(`/custom-lists/${id}`, { params });
  },

  // 管理员自定义列表管理
  adminGetCustomLists: (): Promise<ApiResponse<CustomList[]>> => {
    return adminApi.get('/custom-lists');
  },

  adminGetCustomList: (id: number): Promise<ApiResponse<CustomList>> => {
    return adminApi.get(`/custom-lists/${id}`);
  },

  adminGetCustomListDetail: (id: number, params?: {
    page?: number;
    per_page?: number;
  }): Promise<ApiResponse<CustomListDetailResponse>> => {
    return adminApi.get(`/custom-lists/${id}/detail`, { params });
  },

  adminCreateCustomList: (data: { name: string; show_title?: boolean }): Promise<ApiResponse<CustomList>> => {
    return adminApi.post('/custom-lists', data);
  },

  adminUpdateCustomList: (id: number, data: { name: string; show_title?: boolean }): Promise<ApiResponse<CustomList>> => {
    return adminApi.put(`/custom-lists/${id}`, data);
  },

  adminDeleteCustomList: (id: number): Promise<ApiResponse<void>> => {
    return adminApi.delete(`/custom-lists/${id}`);
  },

  adminAddAppToCustomList: (id: number, data: { app_id: number; sort_order?: number }): Promise<ApiResponse<{message: string}>> => {
    return adminApi.post(`/custom-lists/${id}/apps`, data);
  },

  adminRemoveAppFromCustomList: (id: number, appId: number): Promise<ApiResponse<{message: string}>> => {
    return adminApi.delete(`/custom-lists/${id}/apps/${appId}`);
  },

  adminUpdateCustomListAppSortOrder: (id: number, appId: number, sortOrder: number): Promise<ApiResponse<{message: string}>> => {
    return adminApi.put(`/custom-lists/${id}/apps/${appId}/sort`, { sort_order: sortOrder });
  },
};
